import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  flip,
  offset,
  safePolygon,
  shift,
  useFloating,
  useFocus,
  useHover,
  useInteractions
} from "@floating-ui/react";
import cx from "classnames";
import {
  cloneElement,
  type MouseEventHandler,
  type ReactElement,
  useRef,
  useState
} from "react";

import * as icons from "./Icons";
import TooltipContent from "./TooltipContent";

const getIconSize = iconSize => {
  switch (iconSize) {
    case "x-small":
      return "12px";
    case "small":
      return "16px";
    case "normal":
      return "20px";
    case "large":
      return "24px";
    case "x-large":
      return "32px";
    case "xx-large":
      return "44px";
  }
};

interface IconProps {
  name: string;
  className?: string;
  size?: "x-small" | "small" | "normal" | "large" | "x-large" | "xx-large";
  color?: string;
  hoverColor?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  orientation?: "horizontal" | "vertical";
  width?: string;
  height?: string;
  onAnimationEnd?: () => void;
  "aria-label"?: string;
  tabIndex?: number;
}

export const Icon = ({
  name,
  className,
  size = "normal",
  onClick,
  orientation = "vertical",
  color,
  hoverColor,
  width,
  height,
  onAnimationEnd,
  "aria-label": ariaLabel,
  tabIndex
}: IconProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const IconSvg = icons[name];
  if (!IconSvg) {
    console.warn(`Icon ${name} does not exist`);
    return null;
  }
  const widthValue =
    width || (orientation === "vertical" ? getIconSize(size) : undefined);
  const heightValue =
    height || (orientation === "horizontal" ? getIconSize(size) : undefined);
  return (
    <div
      className={cx(
        className,
        (onClick || tabIndex !== undefined) &&
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4f46e5]"
      )}
      data-testid={name}
      role={onClick ? "button" : ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
      aria-hidden={!onClick && !ariaLabel ? true : undefined}
      tabIndex={tabIndex ?? (onClick ? 0 : undefined)}
      onClick={onClick}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.currentTarget.click();
              }
            }
          : undefined
      }
      onAnimationEnd={onAnimationEnd}
    >
      <IconSvg
        aria-hidden="true"
        className="block overflow-visible"
        width={widthValue}
        height={heightValue}
        size={width}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          ...(color ? { color } : {}),
          ...(isHovered && hoverColor ? { color: hoverColor } : {}),
          width: widthValue,
          height: heightValue || widthValue
        }}
      />
    </div>
  );
};

interface IconWithTooltipProps extends IconProps {
  disabled?: boolean;
  tooltipContent: string;
  tooltipWidth?: string | number;
  hoverDelayMs?: number;
  /** An existing interactive element that should receive focus and contain the icon. */
  trigger?: ReactElement;
}

export const IconWithTooltip = ({
  name,
  className,
  size = "normal",
  onClick,
  disabled = false,
  orientation = "vertical",
  color,
  hoverColor,
  width,
  height,
  tooltipContent: tooltipMarkdown,
  tooltipWidth,
  hoverDelayMs,
  onAnimationEnd,
  "aria-label": ariaLabel,
  tabIndex,
  trigger
}: IconWithTooltipProps) => {
  const [open, setOpen] = useState(false);
  const arrowRef = useRef(null);
  const { x, y, refs, strategy, context } = useFloating({
    placement: "top",
    open,
    middleware: [
      offset({ mainAxis: 8, crossAxis: 4 }),
      flip({
        crossAxis: false,
        padding: {
          top: 65
        }
      }),
      shift({
        padding: {
          top: 65
        }
      }),
      arrow({ element: arrowRef })
    ],
    onOpenChange: setOpen
  });
  const hover = useHover(context, {
    restMs: hoverDelayMs ?? 0,
    handleClose: safePolygon({ blockPointerEvents: false })
  });
  const focus = useFocus(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus
  ]);
  const tooltipWidthClass = tooltipWidth ? `${tooltipWidth}px` : "300px";
  const accessibleLabel = ariaLabel ?? tooltipMarkdown;
  const icon = (
    <Icon
      name={name}
      aria-label={trigger ? undefined : accessibleLabel}
      tabIndex={!trigger && accessibleLabel ? tabIndex : undefined}
      className={cx(
        disabled ? "cursor-not-allowed" : "cursor-pointer",
        className
      )}
      size={size}
      onClick={trigger || disabled ? undefined : onClick}
      orientation={orientation}
      color={disabled ? "#C4C4C4" : color}
      hoverColor={!disabled && hoverColor}
      width={width}
      height={height}
      onAnimationEnd={onAnimationEnd}
    />
  );
  const reference = trigger
    ? cloneElement(
        trigger,
        {
          ...getReferenceProps(trigger.props),
          "aria-label": trigger.props["aria-label"] ?? accessibleLabel,
          ref: refs.setReference
        },
        icon
      )
    : (
        <div ref={refs.setReference} {...getReferenceProps()}>
          {icon}
        </div>
      );

  return (
    <>
      {reference}
      <FloatingPortal>
        {open && (
          <div
            ref={refs.setFloating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0
            }}
            className={cx(
              "flex flex-col gap-2 z-50",
              "p-2 bg-white shadow-lg",
              "border border-solid border-gray-410 rounded"
            )}
            {...getFloatingProps()}
          >
            <TooltipContent
              style={{
                maxWidth: tooltipWidthClass
              }}
              markdown={tooltipMarkdown}
            />
            <FloatingArrow
              height={4}
              width={8}
              context={context}
              ref={arrowRef}
              fill="white"
              stroke="#9b9b9b"
              strokeWidth={1}
            />
          </div>
        )}
      </FloatingPortal>
    </>
  );
};
